import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AddonsService } from '@api-client';

/** TenantConfig module key of the print-plates add-on (ZEY-1818). */
export const PRINT_PLATES_ADDON_KEY = 'printplates';

/**
 * Whether the tenant is entitled to the print-plates add-on (ZEY-2929, epic ZEY-2928 D10).
 *
 * Every print-plates control in the shell (product "Requires printing plate" + print spec,
 * the SO/quotation "New print" checkbox, the line plate badges) is shown only when this is true.
 * It reads `GET /Addons/printplates/active`, the tenant entitlement — never the role-filtered
 * `GET /Modules/enabled`, which lists every module for a superadm.
 *
 * Asked once per session and cached. A failed call reads as inactive (controls stay hidden) and
 * is not cached, so the next screen asks again.
 */
@Injectable({ providedIn: 'root' })
export class PrintPlatesAddonService {
  private readonly addonsService = inject(AddonsService);
  private active$: Observable<boolean> | null = null;

  isActive(): Observable<boolean> {
    if (!this.active$) {
      this.active$ = this.addonsService.addonsKeyActiveGet(PRINT_PLATES_ADDON_KEY).pipe(
        map((result) => result?.active === true),
        catchError(() => {
          this.active$ = null;
          return of(false);
        }),
        shareReplay(1)
      );
    }
    return this.active$;
  }

  /** Promise form for the async ngOnInit screens. */
  isActiveAsync(): Promise<boolean> {
    return new Promise((resolve) => {
      this.isActive().subscribe({ next: resolve, error: () => resolve(false) });
    });
  }
}
